import React, { useState, useEffect, useContext } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import {
  Grid,
  Typography,
  TextField,
  Select,
  InputLabel,
  FormControl,
  MenuItem,
  Box,
  Card,
  CardContent,
  CircularProgress,
  withStyles,
} from '@material-ui/core'
import MuiCheckBox from '@material-ui/core/Checkbox'
import { Controller, useFormContext } from 'react-hook-form'
import NumberFormat from 'react-number-format'
import { map, parseInt } from 'lodash'
import StripeElements from './stripe'
import crypto from 'crypto'
import { UserContext } from '../utils/UserContext'
import { Context } from '../Store'
import { ErrorMessage } from '@hookform/error-message'
import TextErrorMessage from '../utils/TextErrorMessage'
import { createUrl } from '../utils/createListingUrl'
import PaymentLogo from '../PaymentLogo/PaymentLogo'
import { BookingContext } from '../utils/BookingContext'
import { COUNTRY_NAMES_BY_CODE, ISO_3166_COUNTRIES, PAYMENT_TERMS_AND_CONDITION_LINKS } from '../constants'
import ChangePaymentMethodBanner from '../components/ChangePaymentMethodBanner'
import { Alert } from '@material-ui/lab'
import { Info } from '@material-ui/icons'
import UploadReceipt from './uploadReceipt'
import { getWithAuthentication } from '../service/http'
import { sleep } from '../utils/sleep'
import { VerifiedCardIcon } from '../assets/icons/VerifiedCardIcon'
import ErrorMessageComponent from '../utils/ErrorMessage'
import { PaymentPlan } from './paymentPlan'
import MoreDetails from '../utils/moreDetails'
import PricingCard from '../utils/Price/PricingCard'

const Checkbox = withStyles({
  root: {
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&$checked': {
      color: '#0C9D9D',
    },
  },
  checked: {},
})(MuiCheckBox)

export function NumberFormatCustom(props) {
  const { inputRef, onChange, prefix, ...other } = props
  return (
    <NumberFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={(values) => {
        onChange(values.value)
      }}
      thousandSeparator="."
      decimalSeparator=","
      allowNegative={false}
      isNumericString
      prefix={prefix}
    />
  )
}

const PaymentStep = (props) => {
  const { t, i18n } = useTranslation('translations')
  const { user = {} } = useContext(UserContext)
  const [{ property, booking }] = useContext(Context)
  const { control, errors, watch, setError, clearErrors, formState } = useFormContext()
  const values = watch()
  const [adbSub, setAdbSub] = useState(null)
  const { hasCounteroffer } = useContext(BookingContext)
  const [isLoading, setIsLoading] = useState(false)
  const [fundingInstruction, setFundingInstruction] = useState({})

  const [typePaymentValues, setTypePaymentValues] = useState([
    {
      label: t('booking.paymentInfo.typePayment.card'),
      value: 'card',
    },
    {
      label: t('booking.paymentInfo.typePayment.banktransfer'),
      value: 'bank_transfer',
    },
  ])

  useEffect(() => {
    if (user.email) {
      var md5sum = crypto.createHash('md5')
      const md5 = md5sum.update(user.email).digest('hex')
      setAdbSub(md5)
    }
  }, [user])

  useEffect(() => {
    const minPrice = (parseInt(property.price) / 100) * 75
    if (values.tenant_proposal <= minPrice) {
      setError('tenant_proposal', {
        type: 'manual',
        message: 'min75percentage',
        error_code: 'min75percentage',
      })
    } else {
      clearErrors('tenant_proposal')
    }
  }, [values.tenant_proposal])

  useEffect(() => {
    if (!values.card_holder_name) {
      setError('card_holder_name', {
        type: 'manual',
        message: 'this field is required',
      })
    } else {
      clearErrors('card_holder_name')
    }
  }, [values.card_holder_name])

  useEffect(() => {
    if (!values.card_holder_email) {
      setError('card_holder_email', {
        type: 'manual',
        message: 'this field is required',
      })
    } else {
      clearErrors('card_holder_email')
    }
  }, [values.card_holder_email])

  useEffect(() => {
    if (!values.card_holder_address) {
      setError('card_holder_address', {
        type: 'manual',
        message: 'this field is required',
      })
    } else {
      clearErrors('card_holder_address')
    }
  }, [values.card_holder_address])

  useEffect(() => {
    if (!values.card_holder_city) {
      setError('card_holder_city', {
        type: 'manual',
        message: 'this field is required',
      })
    } else {
      clearErrors('card_holder_city')
    }
  }, [values.card_holder_city])

  useEffect(() => {
    if (!values.card_holder_country) {
      setError('card_holder_country', {
        type: 'manual',
        message: 'this field is required',
      })
    } else if (values?.card_holder_country?.length !== 2) {
      setError('card_holder_country', {
        type: 'manual',
        message: 'this field must be 2 characters long',
      })
    } else {
      clearErrors('card_holder_country')
    }
  }, [values.card_holder_country])

  useEffect(() => {
    if (!values.card_holder_zip) {
      setError('card_holder_zip', {
        type: 'manual',
        message: 'this field is required',
      })
    } else {
      clearErrors('card_holder_zip')
    }
  }, [values.card_holder_zip])

  useEffect(() => {
    if (values.payment_type !== 'card' || booking.status === 'PENDING') {
      clearErrors([
        'card_holder_name',
        'card_holder_email',
        'card_holder_address',
        'card_holder_city',
        'card_holder_country',
        'card_holder_zip',
      ])
    } else {
      setError('card_holder_name', {
        type: 'manual',
        message: 'this field is required',
      })
      setError('card_holder_email', {
        type: 'manual',
        message: 'this field is required',
      })
      setError('card_holder_address', {
        type: 'manual',
        message: 'this field is required',
      })
      setError('card_holder_city', {
        type: 'manual',
        message: 'this field is required',
      })
      setError('card_holder_country', {
        type: 'manual',
        message: 'this field is required',
      })
      setError('card_holder_zip', {
        type: 'manual',
        message: 'this field is required',
      })
    }
  }, [values.payment_type])

  useEffect(() => {
    if (booking?.has_pre_payment && !user?.stripe_id) {
      setTypePaymentValues((prev) => prev.filter((item) => item.value !== 'bank_transfer'))
    } else {
      setTypePaymentValues((prev) =>
        prev.length !== 2
          ? [
              {
                label: t('booking.paymentInfo.typePayment.card'),
                value: 'card',
              },
              {
                label: t('booking.paymentInfo.typePayment.banktransfer'),
                value: 'bank_transfer',
              },
            ]
          : prev,
      )
    }
  }, [booking?.has_pre_payment, user?.stripe_id])

  useEffect(() => {
    const getData = async () => {
      try {
        setIsLoading(true)
        await getWithAuthentication(`${process.env.REACT_APP_API_GW_DALGONA_API}/user/create-customer-account`)
        // wait stripe customer id to be created in every services
        await sleep(500)
        getWithAuthentication(`${process.env.REACT_APP_API_GW_DALGONA_API}/user/funding-instructions`)
          .then((res) => {
            setIsLoading(false)
            setFundingInstruction(res.data)
          })
          .catch((error) => {
            setIsLoading(false)
            console.error(error)
          })
      } catch (e) {}
    }
    getData()
  }, [])

  return (
    <>
      {adbSub && (
        <img
          src={`https://tracking.adgoon.it/GL1rQ?adv_sub=${adbSub}`}
          width="1"
          height="1"
          style={{
            display: 'none',
          }}
        />
      )}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h4">
            {hasCounteroffer ? t('booking.paymentInfo.titleCounteroffer') : t('booking.paymentInfo.title')}
          </Typography>
        </Grid>
        <Grid item container spacing={2} wrap="nowrap" xs={12} lg={10}>
          <MoreDetails />
          <Grid item>
            {property.ownerIsPropertyManager || hasCounteroffer ? (
              <Grid item xs={12} lg={10}>
                <Typography variant="subtitle2">{t('booking.paymentInfo.adviceCost.subtitleOwnerIsPropertyManager')}</Typography>
              </Grid>
            ) : (
              <Grid item xs={12} lg={10}>
                <Typography variant="subtitle2">{t('booking.paymentInfo.adviceCost.subtitle')}</Typography>
              </Grid>
            )}
            <Controller
              name="tenant_proposal"
              control={control}
              as={
                <TextField
                  error={errors.tenant_proposal}
                  variant="outlined"
                  fullWidth
                  disabled={property.ownerIsPropertyManager || hasCounteroffer}
                  helperText={
                    property.ownerIsPropertyManager ? t('booking.paymentInfo.adviceCost.subtitleOwnerIsPropertyManager') : ''
                  }
                  InputProps={{
                    readOnly: hasCounteroffer,
                    inputComponent: NumberFormatCustom,
                    inputProps: {
                      prefix: '€',
                      disabled: property.ownerIsPropertyManager,
                    },
                  }}
                />
              }
            />
            <ErrorMessage
              errors={errors}
              name="tenant_proposal"
              render={({ message }) => {
                switch (message) {
                  case 'min75percentage':
                    return (
                      <TextErrorMessage>
                        <Typography variant="body2" color="error">
                          <Trans
                            i18nKey="booking.paymentInfo.adviceCost.min75percentage"
                            values={{
                              price: values.tenant_proposal,
                            }}
                            components={{
                              1: (
                                <a
                                  href={`${createUrl(i18n.language, property.province, property.city_of_reference)}?price=${
                                    values.tenant_proposal
                                  }`}
                                  style={{ textDecoration: 'underline' }}
                                />
                              ),
                            }}
                          />
                        </Typography>
                      </TextErrorMessage>
                    )
                  default:
                    return <TextErrorMessage message={message} />
                }
              }}
            />
          </Grid>
        </Grid>
        <PricingCard />
        <PaymentPlan />
        <Grid item xs={12}>
          <Typography variant="subtitle1">
            {hasCounteroffer ? t('booking.paymentInfo.subtitleTitleCounteroffer') : t('booking.paymentInfo.adviceCost.title')}
          </Typography>
        </Grid>

        <Grid item xs={12} lg={10}>
          <Typography variant="subtitle1">{t('booking.paymentInfo.typePayment.title')}</Typography>
        </Grid>

        <Grid item xs={12} lg={10}>
          <div className="flex items-center" style={{ marginBottom: '0.625rem', gap: '0.625rem' }}>
            <VerifiedCardIcon fill="#0C9D9D" width="2.5rem" height="1.9338rem" />
            <Typography style={{ fontWeight: 400, fontSize: '1rem', color: '#757575', margin: 0 }}>
              {t('booking.paymentInfo.typePayment.confidentText')}
            </Typography>
          </div>
        </Grid>

        <Grid item xs={12} lg={10}>
          <FormControl fullWidth variant="outlined">
            <InputLabel>{t('booking.paymentInfo.typePayment.placeholder')}</InputLabel>
            <Controller
              name="payment_type"
              control={control}
              as={
                <Select
                  name="payment_type"
                  label={t('booking.paymentInfo.typePayment.placeholder')}
                  disabled={user.bank_transfer !== 1 || booking.status === 'PENDING'}
                  error={errors.payment_type}
                >
                  {map(typePaymentValues, (value) => (
                    <MenuItem key={value.value} value={value.value}>
                      {value.label}
                    </MenuItem>
                  ))}
                </Select>
              }
            />
          </FormControl>
        </Grid>

        {booking.status === 'PENDING' && <ChangePaymentMethodBanner bookingId={booking.id} />}

        {values.payment_type === 'card' && booking.status === 'DRAFT' && (
          <>
            <StripeElements {...props} />
            <Grid item xs={12} lg={10}>
              <Typography variant="body1">{t('booking.paymentInfo.cardHolderName')}</Typography>
              <Controller
                name="card_holder_name"
                control={control}
                as={
                  <TextField fullWidth error={formState.touched.card_holder_name && errors.card_holder_name} variant="outlined" />
                }
              />
            </Grid>
            <Grid item xs={12} lg={10}>
              <Typography variant="body1">{t('booking.paymentInfo.cardHolderEmail')}</Typography>
              <Controller
                name="card_holder_email"
                control={control}
                as={
                  <TextField
                    type="email"
                    fullWidth
                    error={formState.touched.card_holder_email && errors.card_holder_email}
                    variant="outlined"
                  />
                }
              />
            </Grid>
            <Grid item xs={12} lg={10}>
              <Typography variant="body1">{t('booking.paymentInfo.cardHolderAddress')}</Typography>
              <Controller
                name="card_holder_address"
                control={control}
                as={
                  <TextField
                    type="text"
                    fullWidth
                    error={formState.touched.card_holder_address && errors.card_holder_address}
                    variant="outlined"
                  />
                }
              />
            </Grid>
            <Grid item xs={12} lg={10}>
              <Typography variant="body1">{t('booking.paymentInfo.cardHolderCity')}</Typography>
              <Controller
                name="card_holder_city"
                control={control}
                as={
                  <TextField
                    type="text"
                    fullWidth
                    error={formState.touched.card_holder_city && errors.card_holder_city}
                    variant="outlined"
                  />
                }
              />
            </Grid>
            <Grid item xs={12} lg={10}>
              <Typography variant="body1">{t('booking.paymentInfo.cardHolderZip')}</Typography>
              <Controller
                name="card_holder_zip"
                control={control}
                as={
                  <TextField
                    type="text"
                    fullWidth
                    error={formState.touched.card_holder_zip && errors.card_holder_zip}
                    variant="outlined"
                  />
                }
              />
            </Grid>
            <Grid item xs={12} lg={10}>
              <FormControl fullWidth variant="outlined">
                <Typography variant="body1">{t('booking.paymentInfo.cardHolderCountry')}</Typography>
                <Controller
                  name="card_holder_country"
                  control={control}
                  error={formState.touched.card_holder_country && errors.card_holder_country}
                  as={
                    <Select>
                      {map(ISO_3166_COUNTRIES, (value) => (
                        <MenuItem key={value} value={value}>
                          {value} - {COUNTRY_NAMES_BY_CODE[value]}
                        </MenuItem>
                      ))}
                    </Select>
                  }
                />
              </FormControl>
            </Grid>
          </>
        )}
        {values.payment_type === 'bank_transfer' && booking?.has_pre_payment && booking.status === 'DRAFT' && (
          <>
            <Grid item xs={12} lg={10}>
              <Alert
                icon={<Info style={{ alignSelf: 'center' }} fontSize="inherit" />}
                severity="warning"
                style={{ color: '#FF7400', backgroundColor: '#FFC1011A' }}
              >
                <Trans
                  i18nKey="booking.paymentInfo.transfer.alert"
                  components={{
                    1: <strong>{t('booking.paymentInfo.transfer.alertStrong')}</strong>,
                  }}
                />
              </Alert>
            </Grid>
            {isLoading ? (
              <Card sx={{ minWidth: 275 }} style={{ width: '100%', textAlign: 'center', margin: '0rem 0.625rem' }}>
                <CardContent>
                  <CircularProgress />
                </CardContent>
              </Card>
            ) : (
              <Grid item xs={12} lg={10}>
                <Card sx={{ minWidth: 275 }}>
                  <CardContent>
                    <Typography style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Box fontWeight="fontWeightBold" fontSize="1rem">
                        {t('fundingInstruction.account')}
                      </Box>
                      <Box fontWeight="fontWeightRegular" fontSize="1rem">
                        {fundingInstruction?.account_holder_name}
                      </Box>
                    </Typography>
                    <Typography style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Box fontWeight="fontWeightBold" fontSize="1rem">
                        Bic
                      </Box>
                      <Box fontWeight="fontWeightRegular" fontSize="1rem">
                        {fundingInstruction?.bic}
                      </Box>
                    </Typography>
                    <Typography style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Box fontWeight="fontWeightBold" fontSize="1rem">
                        {t('fundingInstruction.nation')}
                      </Box>
                      <Box fontWeight="fontWeightRegular" fontSize="1rem">
                        {fundingInstruction?.country}
                      </Box>
                    </Typography>
                    <Typography style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Box fontWeight="fontWeightBold" fontSize="1rem">
                        IBAN
                      </Box>
                      <Box fontWeight="fontWeightRegular" fontSize="1rem">
                        {fundingInstruction?.iban}
                      </Box>
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
            <Grid item xs={12} lg={10}>
              <Typography variant="body1">{t('booking.paymentInfo.transfer.uploadInputHeader')}</Typography>
              <UploadReceipt booking_id={booking.id} setError={setError} clearErrors={clearErrors} />
            </Grid>
          </>
        )}
        <Grid item xs={12} lg={10}>
          <Typography variant="body1">{t('booking.paymentInfo.collaborateWithUs')}:</Typography>
          <PaymentLogo />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <Controller
              name="terms_and_condition"
              control={control}
              defaultValue={false}
              rules={{ required: true }}
              render={(field) => {
                return (
                  <Box className="flex items-center" style={{ marginTop: '1.5rem' }}>
                    <Checkbox onChange={field.onChange} checked={field.value} />
                    <Grid item xs={12} lg={10}>
                      <Typography style={{ fontWeight: 400, fontSize: '1rem', color: '#757575', marginBottom: 0 }}>
                        <Trans
                          i18nKey="booking.paymentInfo.termsAndCondition.title"
                          components={{
                            1: (
                              <a
                                style={{ fontWeight: 700, fontSize: '1rem', color: '#0C9D9D', cursor: 'pointer' }}
                                href={PAYMENT_TERMS_AND_CONDITION_LINKS}
                                target="_blank"
                                rel="noreferrer"
                              />
                            ),
                          }}
                        />
                      </Typography>
                      <ErrorMessage errors={errors} name="terms_and_condition" render={ErrorMessageComponent} />
                    </Grid>
                  </Box>
                )
              }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Box paddingTop={2} />
          <a
            target="_blank"
            rel="noreferrer"
            style={{ fontWeight: 300, cursor: 'pointer', textDecoration: 'underline' }}
            href={t('booking.checkFaqLink')}
          >
            {' '}
            <Typography variant="h5">{t('booking.checkFaq')}</Typography>{' '}
          </a>
        </Grid>
      </Grid>
    </>
  )
}

export default PaymentStep
